<template>
  <div class="site-container">
    <!-- Background color layer (bottom) -->
    <div
      class="site-background-color-layer"
      :style="siteBackgroundColorStyle"
    ></div>
    <!-- Background image layer (middle) -->
    <div
      v-if="siteBackgroundEnabled"
      class="site-background-image-layer"
      :style="siteBackgroundImageStyle"
    ></div>
    <div class="site-content">
      <!-- Header components view -->
      <div
        v-for="component in header"
        :key="component.idx"
        class="component-wrapper"
      >
        <component :is="component.id" :component="component"></component>
      </div>

      <!-- Content components view -->
      <div
        v-for="component in content"
        :key="component.idx"
        class="component-wrapper"
      >
        <component :is="component.id" :component="component"></component>
      </div>

      <!-- Footer components view -->
      <div
        v-for="component in footer"
        :key="component.idx"
        class="component-wrapper"
      >
        <component :is="component.id" :component="component"></component>
      </div>

      <footnote />
    </div>
    <!-- End site-content -->
  </div>
  <!-- End site-container -->
</template>

<script>
import MenuBar from "./ViewComponents/Header/MenuBar.vue";
import Banner from "./ViewComponents/Header/Banner.vue";
import TextOneCol from "./ViewComponents/Shared/TextOneCol.vue";
import TextTwoCol from "./ViewComponents/Shared/TextTwoCol.vue";
import TextThreeCol from "./ViewComponents/Shared/TextThreeCol.vue";
import ImageComponent from "./ViewComponents/Shared/Image.vue";
import ProgressComponent from "./ViewComponents/Shared/Progress/Progress.vue";
import AwardsComponent from "./ViewComponents/Shared/Awards/Awards.vue";
import ArticlesPreviewComponent from "./ViewComponents/Shared/Articles/ArticlesPreview.vue";
import ArticlesComponent from "./ViewComponents/Shared/Articles/Articles.vue";
import RewardsPreviewComponent from "./ViewComponents/Shared/Rewards/RewardsPreview.vue";
import RewardsComponent from "./ViewComponents/Shared/Rewards/Rewards.vue";
import ClaimsComponent from "./ViewComponents/Shared/Claims/Claims.vue";
import QuizzesComponent from "./ViewComponents/Shared/Quizzes/Quizzes.vue";
import FooterMenu from "./ViewComponents/Footer/Menu.vue";
import HeadingSection from "./ViewComponents/Shared/HeadingSection.vue";
import PageHeadings from "./ViewComponents/Shared/BasicPage/PageHeadings.vue";
import PageText from "./ViewComponents/Shared/BasicPage/PageText.vue";
import AuthSignIn from "./ViewComponents/Shared/Authentication/SignIn.vue";
import AuthSignUp from "./ViewComponents/Shared/Authentication/SignUp.vue";
import Footnote from "./Footnote.vue";
import Avatar from "./ViewComponents/Shared/Profile/Avatar.vue";
import ProfileDetail from "./ViewComponents/Shared/Profile/ProfileDetail.vue";

export default {
  components: {
    MenuBar,
    Banner,
    ProgressComponent,
    AwardsComponent,
    FooterMenu,
    ArticlesPreviewComponent,
    ArticlesComponent,
    ImageComponent,
    RewardsComponent,
    RewardsPreviewComponent,
    ClaimsComponent,
    QuizzesComponent,
    TextOneCol,
    TextTwoCol,
    TextThreeCol,
    HeadingSection,
    PageHeadings,
    PageText,
    AuthSignIn,
    AuthSignUp,
    Footnote,
    ProfileDetail,
    Avatar
  },
  created() {},
  data: () => ({}),
  props: ["selectedPageTemplate"],
  computed: {
    grid() {
      return this.$store.getters.grid;
    },
    programId() {
      return this.$store.getters.programId;
    },
    webTheme() {
      return this.$store.getters.currentProgram.webTheme;
    },
    // selectedPageTemplate() {
    //   return this.$store.getters.selectedPageTemplate
    // },
    designDrawer() {
      return this.$store.getters.designDrawer;
    },
    authentication() {
      switch (this.selectedPageTemplate) {
        case "Signup":
          return this.webTheme.auth.signup.components;
        default:
          return this.webTheme.auth.signin.components;
      }
    },
    header() {
      return this.webTheme.header.components;
    },
    customPages() {
      // Ensure pages is always an array
      if (!this.webTheme.content.pages) {
        return [];
      }
      if (Array.isArray(this.webTheme.content.pages)) {
        return this.webTheme.content.pages;
      }
      // Legacy support
      if (this.webTheme.content.pages.components) {
        return [
          {
            id: "default",
            name: "Basic Page",
            components: this.webTheme.content.pages.components
          }
        ];
      }
      return [];
    },
    content() {
      switch (this.selectedPageTemplate) {
        case "Articles":
          return this.webTheme.content.articles.components;
        case "Home":
          return this.webTheme.content.home.components;
        case "Claims":
          return this.webTheme.content.claims.components;
        case "Quizzes":
          return this.webTheme.content.quizzes.components;
        case "Rewards":
          return this.webTheme.content.rewards.components;
        case "Profile":
          return this.webTheme.content.profile.components;
        default: {
          // Check if it's a custom basic page
          const customPage = this.customPages.find(
            page => page.name === this.selectedPageTemplate
          );
          if (customPage) {
            return customPage.components;
          }
          return this.webTheme.content.home.components;
        }
      }
    },
    footer() {
      return this.webTheme.footer.components;
    },
    siteBackgroundEnabled() {
      const bg = this.webTheme.colors.siteBackground;
      return bg && bg.enabled && bg.image;
    },
    siteBackgroundColorStyle() {
      // Background color layer - sits below image layer
      // Note: z-index set in CSS to -2
      return `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100%;
        min-height: 100vh;
        background-color: ${this.webTheme.colors.canvas.hexa};
      `;
    },
    siteBackgroundImageStyle() {
      const bg = this.webTheme.colors.siteBackground;
      if (!bg || !bg.enabled || !bg.image) return "";

      // Calculate height for the background area (acts as a clipping mask)
      const height =
        bg.heightContext === "vh"
          ? `${bg.heightValue.vh}vh`
          : `${bg.heightValue.px}px`;

      // For no-repeat mode:
      // - Use 100vw width (viewport width) with auto height to maintain aspect ratio
      // - This ensures consistent sizing regardless of position (fixed/absolute)
      // - Height slider controls the container clipping height
      // - Image crops from bottom when height is reduced
      // For repeat mode: let it tile at natural size
      let backgroundSize = "auto";
      let extraStyle = "";
      if (bg.repeat === "no-repeat") {
        backgroundSize = `100vw auto`;
        // Set height on the background layer to create clipping
        // Add 1px extra to prevent sub-pixel rendering gaps
        extraStyle = `height: calc(${height} + 1px);`;
      }

      // Position handling: fixed stays fixed, scroll uses absolute
      const position = bg.attachment === "fixed" ? "fixed" : "absolute";

      // Vertical alignment determines background-position
      const verticalAlign = bg.verticalAlign || "top";
      const backgroundPosition = `${verticalAlign} center`;

      // Build gradient overlay if screen settings exist
      let backgroundImage = `url('${bg.image}')`;
      if (bg.screen) {
        const gradient = this.siteBackgroundGradient;
        if (gradient) {
          backgroundImage = `linear-gradient(${gradient}), url('${bg.image}')`;
        }
      }

      // Build background layer style
      // Force width to 100vw to ensure consistent sizing in both modes
      // Note: z-index set in CSS to -1
      return `
        position: ${position};
        width: 100vw;
        background-image: ${backgroundImage};
        background-repeat: ${bg.repeat};
        background-position: ${backgroundPosition};
        background-size: ${backgroundSize};
        ${extraStyle}
      `;
    },
    siteBackgroundGradient() {
      const bg = this.webTheme.colors.siteBackground;
      if (!bg || !bg.screen) return null;

      const screen = bg.screen;

      if (screen.type === "gradient") {
        // Calculate gradient based on direction and vertical alignment
        // The height percentage represents the SIZE of the gradient coverage
        const verticalAlign = screen.verticalAlign || "top";

        if (verticalAlign === "top") {
          // Gradient anchored to top, covering X% from the top
          if (screen.direction === "to bottom") {
            // Gradient flows downward from top for X%, rest transparent
            return `${screen.direction}, rgba(${screen.fromColor.rgba.r}, ${screen.fromColor.rgba.g}, ${screen.fromColor.rgba.b}, ${screen.fromColor.rgba.a}) 0%, rgba(${screen.toColor.rgba.r}, ${screen.toColor.rgba.g}, ${screen.toColor.rgba.b}, ${screen.toColor.rgba.a}) ${screen.height}%, transparent ${screen.height}%`;
          } else {
            // "to top" - gradient flows upward within top X%, transparent below
            // With "to top", gradient line goes bottom-to-top, so color stops are:
            // - 0% is at bottom of element
            // - 100% is at top of element
            // We want gradient in top X%, so: transparent from bottom up to (100-X)%, then gradient
            const transparentEnd = 100 - screen.height;
            return `${screen.direction}, transparent 0%, transparent ${transparentEnd}%, rgba(${screen.fromColor.rgba.r}, ${screen.fromColor.rgba.g}, ${screen.fromColor.rgba.b}, ${screen.fromColor.rgba.a}) ${transparentEnd}%, rgba(${screen.toColor.rgba.r}, ${screen.toColor.rgba.g}, ${screen.toColor.rgba.b}, ${screen.toColor.rgba.a}) 100%`;
          }
        } else {
          // Gradient anchored to bottom, covering X% from the bottom
          const startPoint = 100 - screen.height;
          if (screen.direction === "to bottom") {
            // Transparent at top, gradient in bottom X%
            return `${screen.direction}, transparent 0%, transparent ${startPoint}%, rgba(${screen.fromColor.rgba.r}, ${screen.fromColor.rgba.g}, ${screen.fromColor.rgba.b}, ${screen.fromColor.rgba.a}) ${startPoint}%, rgba(${screen.toColor.rgba.r}, ${screen.toColor.rgba.g}, ${screen.toColor.rgba.b}, ${screen.toColor.rgba.a}) 100%`;
          } else {
            // "to top" - gradient flows upward from bottom edge for X%, rest transparent
            return `${screen.direction}, rgba(${screen.fromColor.rgba.r}, ${screen.fromColor.rgba.g}, ${screen.fromColor.rgba.b}, ${screen.fromColor.rgba.a}) 0%, rgba(${screen.toColor.rgba.r}, ${screen.toColor.rgba.g}, ${screen.toColor.rgba.b}, ${screen.toColor.rgba.a}) ${screen.height}%, transparent ${screen.height}%`;
          }
        }
      } else if (screen.type === "screen") {
        // Build solid screen overlay
        return `${screen.direction}, rgba(${screen.color.rgba.r}, ${screen.color.rgba.g}, ${screen.color.rgba.b}, ${screen.color.rgba.a}), rgba(${screen.color.rgba.r}, ${screen.color.rgba.g}, ${screen.color.rgba.b}, ${screen.color.rgba.a})`;
      }

      return null;
    },
    // Member site specific models
    currentUser() {
      return this.$store.getters.currentUser;
    }
  }
};
</script>

<style scoped>
.site-container {
  position: relative;
  z-index: 0;
}

.site-background-color-layer {
  pointer-events: none;
  z-index: 0 !important;
}

.site-background-image-layer {
  pointer-events: none;
  overflow: hidden;
  z-index: 1 !important;
}

.site-content {
  position: relative;
  z-index: 10;
}

.dotted {
  border: 1px dashed rgb(184, 184, 184) !important;
}

.component-wrapper {
  position: relative;
}
</style>
